howspace.components.dropdown = (() => {
    'use strict';

    const MIN_WINDOW_WIDTH_FOR_SIDE_MENU = 768;
    const MENU_ITEM_SELECTOR = '[role="menuitem"], [role="menuitemradio"], [role="menuitemcheckbox"]';
    const HEADER_ARROW_FOCUSABLE_SELECTOR = 'input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])';
    const HEADER_TAB_FOCUSABLE_SELECTOR = 'button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])';

    const getNavigableElements = (menu, headerFocusableSelector) => {
        if (!menu) {
            return [];
        }

        const header = menu.querySelector(':scope > .menu-header');
        const menuItems = menu.querySelector(':scope > .menu-items');
        const elements = [
            ...(header ? $(header).find(headerFocusableSelector).toArray() : []),
            ...(menuItems ? $(menuItems).find(`${MENU_ITEM_SELECTOR}, [data-toggle="dropdown"]`).toArray() : [])
        ];

        return elements.filter(element => {
            const $element = $(element);
            return $element.is(':visible') && !$element.is('.disabled:not(.tooltip), [disabled]');
        });
    };

    const getArrowNavigableMenuItems = menu => {
        return getNavigableElements(menu, HEADER_ARROW_FOCUSABLE_SELECTOR);
    };

    const getTabNavigableMenuItems = menu => {
        return getNavigableElements(menu, HEADER_TAB_FOCUSABLE_SELECTOR);
    };

    const findNextMenuItem = (element, navigableMenuItems) => {
        const index = navigableMenuItems.indexOf(element);
        if (index >= 0) {
            return navigableMenuItems[index + 1] ?? null;
        }
        return null;
    };

    const findPreviousMenuItem = (element, navigableMenuItems) => {
        const index = navigableMenuItems.indexOf(element);
        if (index >= 0) {
            return navigableMenuItems[index - 1] ?? null;
        }
        return null;
    };

    const moveFocusToNext = (dropdown, navigableMenuItems) => {
        navigableMenuItems ??= getArrowNavigableMenuItems(dropdown.querySelector(':scope .howspace-menu'));

        if (navigableMenuItems.length === 0) {
            return;
        }

        if (!navigableMenuItems.includes(document.activeElement)) {
            // Put focus on the first visible navigable item.
            navigableMenuItems[0].focus();
            return;
        }

        const nextMenuItem = findNextMenuItem(document.activeElement, navigableMenuItems);
        if (nextMenuItem === null) {
            // Put focus on the first visible navigable item.
            navigableMenuItems[0].focus();
            return;
        }

        // Put focus on the next menu item.
        nextMenuItem.focus();
    }

    const moveFocusToPrevious = (dropdown, navigableMenuItems) => {
        navigableMenuItems ??= getArrowNavigableMenuItems(dropdown.querySelector(':scope .howspace-menu'));

        if (navigableMenuItems.length === 0) {
            return;
        }

        if (!navigableMenuItems.includes(document.activeElement)) {
            // Put focus on the last visible navigable item.
            navigableMenuItems[navigableMenuItems.length - 1].focus();
            return;
        }

        const previousMenuItem = findPreviousMenuItem(document.activeElement, navigableMenuItems);
        if (previousMenuItem === null) {
            // Put focus on the last visible navigable item.
            navigableMenuItems[navigableMenuItems.length - 1].focus();
            return;
        }

        // Put focus on the previous navigable item.
        previousMenuItem.focus();
    }

    const getActiveExpandedDropdown = () => {
        const activeElement = document.activeElement;
        const containingDropdown = activeElement?.closest('.dropdown.expanded');

        if (containingDropdown) {
            return containingDropdown;
        }

        const expandedDropdowns = Array.from(document.querySelectorAll('.dropdown.expanded'));
        // Return the last expanded dropdown in the DOM
        return expandedDropdowns[expandedDropdowns.length - 1] ?? null;
    };

    const checkMouseOver = (e) => {
        const dropdown = e.target.closest('.dropdown[data-expand-on-hover="true"]');
        if (!dropdown) {
            return;
        }

        // Cancel pending close when user moves back (covers the gap case)
        clearHoverCloseTimer(dropdown);

        const toggle = e.target.closest('[data-toggle="dropdown"]');
        if (toggle && toggle.getAttribute('type') === 'search') {
            return;
        }

        if (!dropdown.classList.contains('expanded')) {
            expandMenu(dropdown);
        }
    };

    const checkMouseOut = (e) => {
        const dropdown = e.target.closest('.dropdown[data-expand-on-hover="true"]');
        if (!dropdown) {
            return;
        }

        // If moving within the same dropdown, ignore
        if (dropdown.contains(e.relatedTarget)) {
            return;
        }

        // Delay closing so small gaps don’t instantly collapse the menu
        scheduleHoverClose(dropdown);
    }

    const checkKeyDown = e => {
        const dropdown = getActiveExpandedDropdown();
        if (dropdown === null) {
            return;
        }

        const arrowNavigableMenuItems = getArrowNavigableMenuItems(dropdown.querySelector(':scope .howspace-menu'));
        const tabNavigableMenuItems = getTabNavigableMenuItems(dropdown.querySelector(':scope .howspace-menu'));

        switch (e.key) {
            case 'Enter' :
            case ' ' :
                if (e.target.dataset.toggle === 'dropdown') {
                    e.preventDefault();
                    e.stopPropagation();
                }
                break;

            case 'Tab' :
                e.preventDefault();
                if (e.shiftKey) {
                    moveFocusToPrevious(dropdown, tabNavigableMenuItems);
                } else {
                    moveFocusToNext(dropdown, tabNavigableMenuItems);
                }
                break;

            case 'ArrowDown' :
                e.preventDefault();
                moveFocusToNext(dropdown, arrowNavigableMenuItems);
                break;

            case 'ArrowUp' :
                e.preventDefault();
                moveFocusToPrevious(dropdown, arrowNavigableMenuItems);
                break;

            case 'Home' :
                // Put focus on the first menu item.
                e.preventDefault();
                arrowNavigableMenuItems[0]?.focus();

                break;

            case 'End' :
                // Put focus on the last menu item.
                e.preventDefault();
                arrowNavigableMenuItems[arrowNavigableMenuItems.length - 1]?.focus();
                break;
        }
    };

    const checkKeyUp = e => {
        const activeElement = document.activeElement;
        const dropdown = activeElement?.closest('.dropdown');
        const expandedDropdown = activeElement?.closest('.dropdown.expanded');
        if (dropdown === null) {
            return;
        }

        const isControlledCollapse = e.target.getAttribute('aria-controls') !== null;
        const isToggle = e.target.dataset.toggle === 'dropdown' || e.target.closest('[data-toggle="dropdown"]') !== null;

        switch (e.key) {
            case 'Enter' :
            case ' ' :
                if (activeElement?.dataset.toggle === 'dropdown') {
                    expandMenu(dropdown, true);
                }
                break;

            case 'Escape' :
                if (expandedDropdown) {
                    collapseMenu(expandedDropdown);
                    expandedDropdown.querySelector('[data-toggle="dropdown"]')?.focus();
                }
                break;

            case 'ArrowRight' :
                if (isControlledCollapse) {
                    e.preventDefault();
                    howspace.components.collapse.expand(activeElement);
                }

                if (isToggle && e.target.type !== 'search') {
                    e.preventDefault();
                    const subDropdown = activeElement.closest('.dropdown');
                    if (subDropdown) {
                        expandMenu(subDropdown, true);
                    }
                }

                break;

            case 'ArrowLeft' :
                if (isControlledCollapse) {
                    e.preventDefault();
                    howspace.components.collapse.collapse(activeElement);
                }

                if (dropdown.contains(activeElement)) {
                    e.preventDefault();
                    if (dropdown.closest('.howspace-menu')) {
                        // Close only if this is not the top level dropdown.
                        collapseMenu(dropdown);
                        dropdown.closest('.dropdown')?.querySelector('[data-toggle="dropdown"]')?.focus()
                    }
                }

                break;
        }
    };

    const checkPointerDown = e => {
        const dropdown = e.target.closest('.dropdown');
        if (dropdown === null) {
            document.querySelectorAll('.dropdown.expanded').forEach(d => {
                collapseMenu(d);
            });
            return;
        }

        if ($(e.target).is('input') || e.target.closest('[data-toggle="dropdown"]')) {
            return;
        }

        const menuItem = e.target.closest(MENU_ITEM_SELECTOR);
        const isMenuHeaderInteraction = e.target.closest('.menu-header') !== null;
        const isMenuItem = menuItem !== null;
        const isDisabled = menuItem?.classList.contains('disabled') ?? false;
        const isControlledCollapse = menuItem?.getAttribute('aria-controls') != null;
        const isInternalCollapse = isControlledCollapse && dropdown.contains(document.getElementById(menuItem.getAttribute('aria-controls')));

        if (
            // User didn't click on a menu item.
            !isMenuItem ||
            // The menu item is disabled.
            isDisabled ||
            // The menu item controls a collapse and the collapse is inside the same dropdown
            (isControlledCollapse && isInternalCollapse)
        ) {
            if (!isMenuHeaderInteraction) {
                e.preventDefault();
            }
            e.stopPropagation();
            e.target.dataset.preventClick = true;
        }
    };

    const checkClick = e => {
        const dropdown = e.target.closest('.dropdown');
        if (dropdown === null) {
            return;
        }

        const isDropdownToggle = e.target.dataset.toggle === 'dropdown' || e.target.closest('[data-toggle="dropdown"]') !== null;
        const isControlledCollapse = e.target.getAttribute('aria-controls') !== null;
        const isInternalCollapse = isControlledCollapse && dropdown.contains(document.getElementById(e.target.getAttribute('aria-controls')));

        if ($(e.target).is('input')) {
            return;
        }

        // User clicked on a menu item that controls an internal collapse, don't close the dropdown in this case.
        if ((isControlledCollapse && isInternalCollapse) || isDropdownToggle) {
            return;
        }

        if (e.target.dataset.preventClick === 'true') {
            delete e.target.dataset.preventClick;
            return;
        }

        // User clicked on a non-disabled menu item. By default, we close the menu when this happens.
        // Developers can override this feature by adding event listener to the menu item button
        // and stop the event propagation in the handler.
        collapseMenu(dropdown);

        // Put focus to the dropdown toggle element (if one is found).
        dropdown.querySelector('[data-toggle="dropdown"][aria-expanded="true"]')?.focus();
    };

    const expandMenu = (dropdown, focusFirstElement = true) => {
        if (dropdown instanceof jQuery) {
            dropdown = dropdown[0];
        }

        const parentMenu = dropdown.closest('.howspace-menu');

        // If the dropdown is inside another dropdown and the window is narrow enough, let's open as a collapse.
        if (window.innerWidth < MIN_WINDOW_WIDTH_FOR_SIDE_MENU && parentMenu) {
            dropdown.classList.add('collapse-menu');
        }

        // If this dropdown lives inside another dropdown's menu, only close sibling dropdowns
        // within the SAME parent menu level (and never the parent dropdown itself).
        if (parentMenu) {
            // Close other expanded dropdowns inside the same menu (siblings)
            parentMenu.querySelectorAll('.dropdown.expanded').forEach(d => {
                if (d !== dropdown) {
                    collapseMenu(d);
                }
            });
        } else {
            // This is a top-level dropdown => close other top-level expanded dropdowns
            document.querySelectorAll('.dropdown.expanded').forEach(d => {
                if (d !== dropdown) {
                    collapseMenu(d);
                }
            });
        }

        const dropdownToggle = dropdown.querySelector('[data-toggle="dropdown"]');
        const dropdownMenu = dropdown.querySelector('.howspace-menu');
        const dropdownMenuItems = dropdownMenu.querySelector('.menu-items');

        if (document.querySelector('.dropdown.expanded') === null) {
            // There are no expanded dropdowns yet, add the event listeners.
            document.body.addEventListener('pointerdown', checkPointerDown);
            document.body.addEventListener('click', checkClick);
            document.body.addEventListener('keydown', checkKeyDown);
            document.body.addEventListener('keyup', checkKeyUp);
            if (window.innerWidth >= MIN_WINDOW_WIDTH_FOR_SIDE_MENU) {
                document.body.addEventListener('mouseover', checkMouseOver);
                document.body.addEventListener('mouseout', checkMouseOut);
            }
        }

        howspace.dispatchEvent(dropdown, howspace.components.dropdown.OPENING);

        if (!dropdown.classList.contains('collapse-menu')) {
            howspace.popover.open(dropdown, dropdownMenu, dropdownMenuItems);
        }

        dropdown.classList.add('expanded');
        dropdownToggle.setAttribute('aria-expanded', 'true');

        if (focusFirstElement && dropdownToggle.matches(':focus-visible')) {
            dropdownMenu.querySelector(`${MENU_ITEM_SELECTOR}, [data-toggle="dropdown"]`)?.focus();
        }

        if (dropdown.classList.contains('collapse-menu')) {
            howspace.animate(dropdownMenu, 'slideDown', 350);
        }
    };

    const collapseMenu = dropdown => {
        if (dropdown instanceof jQuery) {
            dropdown = dropdown[0];
        }
        if (!dropdown) {
            return;
        }

        const dropdownMenu = dropdown.querySelector('.howspace-menu');
        if (!dropdown.classList.contains('collapse-menu')) {
            howspace.popover.close(dropdown, dropdownMenu);
        }

        dropdown.classList.remove('expanded');

        if (dropdown.classList.contains('collapse-menu')) {
            howspace.animate(dropdownMenu, 'slideUp', 350, () => {
                dropdown.classList.remove('collapse-menu')
            });
        }

        const dropdownToggle = dropdown.querySelector('[data-toggle="dropdown"]');
        dropdownToggle.setAttribute('aria-expanded', 'false');

        if (document.querySelector('.dropdown.expanded') === null) {
            // There are no more expanded dropdowns, remove the event listeners.
            document.body.removeEventListener('pointerdown', checkPointerDown);
            document.body.removeEventListener('click', checkClick);
            document.body.removeEventListener('keydown', checkKeyDown);
            document.body.removeEventListener('keyup', checkKeyUp);
            if (window.innerWidth >= MIN_WINDOW_WIDTH_FOR_SIDE_MENU) {
                document.body.removeEventListener('mouseover', checkMouseOver);
                document.body.removeEventListener('mouseout', checkMouseOut);
            }
        }
    };

    // Always listen to click on body. If click happens on dropdown toggle then toggle the dropdown menu.
    document.body.addEventListener('click', e => {
        let elem = e.target;
        if (elem.dataset.toggle !== 'dropdown') {
            elem = e.target.closest('[data-toggle="dropdown"]');
            if (elem === null) {
                return;
            }
        }

        if (elem.getAttribute('type') === 'search') {
            // Search input can control a dropdown, but clicking the input should not expand/collapse the menu.
            return;
        }

        const dropdown = elem.closest('.dropdown');

        if (elem.getAttribute('aria-expanded') === 'true') {
            collapseMenu(dropdown);
        } else {
            expandMenu(dropdown, false);
        }
    });

    const hoverCloseTimers = new WeakMap();
    const clearHoverCloseTimer = dropdown => {
        const t = hoverCloseTimers.get(dropdown);
        if (t) {
            clearTimeout(t);
            hoverCloseTimers.delete(dropdown);
        }
    };

    const scheduleHoverClose = dropdown => {
        clearHoverCloseTimer(dropdown);

        const t = setTimeout(() => {
            collapseMenu(dropdown);
        }, 500);

        hoverCloseTimers.set(dropdown, t);
    };

    return {
        OPENING: 'dropdown-opening',
        close : dropdown => {
            collapseMenu(dropdown);
        },
        open : (dropdown, focusFirstElement = true) => {
            expandMenu(dropdown, focusFirstElement);
        }
    };

})();
